import { type DocNode } from '@atlaskit/adf-schema/doc';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { EditorContextPayloadData } from '@atlaskit/rovo-triggers/types';
import { type ArtifactContextPayloadData } from '@atlaskit/rovo-triggers/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { type SendMessageSearchArtifact } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import type { ReadContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import { applyCreationContextContract } from './applyCreationContextContract';
import { getChatContextContentType } from './getChatContextContentType';
import { getCreationContextBlocks } from './getCreationContextBlocks';
import { getCreationContextExperience } from './getCreationContextExperience';
import { handleEditorContextAnalytics } from './handleEditorContextAnalytics';
import type { ChatStoreContainerProps } from './types';

type ChatContextStoreActions = ChatStoreContainerProps['chatContextStoreActions'];

/**
 * Result shape produced by {@link populateSendMessageContext}. Field types are
 * derived directly from the chat-context store getters so the concrete context
 * shapes (e.g. `editorContext.isViewMode`, `editorContext.dynamicUiType`) are
 * preserved for downstream consumers. Made explicit to satisfy
 * `--isolatedDeclarations` now that this helper is exported from its own file.
 */
type PopulateSendMessageContextResult = {
	browserContext: Awaited<ReturnType<ChatContextStoreActions['getBrowserContext']>>;
	editorContext: Awaited<ReturnType<ChatContextStoreActions['getEditorContext']>> | undefined;
	whiteboardContext:
		| Awaited<ReturnType<ChatContextStoreActions['getWhiteboardContext']>>
		| undefined;
	slidesContext: Awaited<ReturnType<ChatContextStoreActions['getSlidesContext']>> | undefined;
	databaseContext: Awaited<ReturnType<ChatContextStoreActions['getDatabaseContext']>> | undefined;
	dashboardEditorContext: Awaited<ReturnType<ChatContextStoreActions['getDashboardEditorContext']>>;
	additionalContext: {
		spaceCreateContext: Awaited<ReturnType<ChatContextStoreActions['getSpaceCreateContext']>>;
		creationContext: Record<string, unknown>;
		userTimezone: string;
		search_artifact: SendMessageSearchArtifact | undefined;
	};
	computedExperience: CreationContextParams['experience'];
	aiFeatureContext: ReturnType<ChatContextStoreActions['getAIFeatureContext']>;
	jiraWorkflowContext: ReturnType<ChatContextStoreActions['getJiraWorkflowContext']>;
	dashboardContext: ReturnType<ChatContextStoreActions['getDashboardContext']>;
	artifactContext: ArtifactContextPayloadData | undefined;
	useCurrentBrowsingContext: ReturnType<ChatContextStoreActions['useCurrentBrowsingContext']>;
};

export const populateSendMessageContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	source: string | undefined,
	prompt: DocNode | undefined,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	creationContextParams: CreationContextParams | undefined,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	activeTargetId?: string | number,
	lastAssistantMessageDynamicUiType?: string,
	skipEditorContext?: boolean,
	isRemixAction?: boolean,
	searchArtifact?: SendMessageSearchArtifact,
	additionalContextReceiverId?: ReadContentCommandProperties['receiverId'],
	editorContextFallback?: EditorContextPayloadData,
): Promise<PopulateSendMessageContextResult> => {
	// Scoped to Confluence: the contract comes from the Confluence experience routing spec, and
	// Jira runs its own remix-edit rollout with a different payload shape.
	const isCreationContextContractEnabled =
		productKey === 'confluence' && fg('confluence_aifc_experience_rearchitecture');

	// When the gate is on and the user is in the CWR staging-area on the first prompt with no
	// eligible source, getContextRequirements returns false for contentContext='staging-area'
	// (it ignores userPreference). To ensure the draft editor context is always fetched, fetch
	// with contentContext='global' which respects userPreference=true. The effective content
	// context for all other purposes (isStagingArea, experience, etc.) remains 'staging-area'.
	const isStagingAreaFirstPromptFix =
		fg('confluence_aifc_new_live_page_chat_context') &&
		contentContextValue === 'staging-area' &&
		isFirstPrompt;

	const fetchedEditorContext = skipEditorContext
		? undefined
		: await chatContextStoreActions.getEditorContext({
				isFirstPrompt,
				source,
				// For staging-area first prompts, getContextRequirements ignores userPreference.
				// Use 'global' so userPreference=true is respected and the draft context is fetched.
				contentContext: isStagingAreaFirstPromptFix ? 'global' : contentContextValue,
				addCurrentPageAsContextUserPreference: isStagingAreaFirstPromptFix
					? true
					: !!addCurrentPageAsContextUserPreference,
				activeTargetId,
				prompt,
			});
	// The Remix entry point already has an editor context when it selects REMIX_CUSTOM_EDIT. A
	// send-time bridge read can still transiently return undefined; retain the entry-point context
	// so the required PAGE_CONTEXT is not dropped from the request.
	const editorContext =
		fetchedEditorContext ??
		(!skipEditorContext &&
		productKey === 'confluence' &&
		fg('aifc_remix_custom_page_edit') &&
		creationContextParams?.experience === 'remix_custom_edit' &&
		editorContextFallback
			? { ...editorContextFallback }
			: undefined);

	const currentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);

	// If there's no editor context available and the current target CAN
	// provide context, send analytics to track this error state
	if (!editorContext && !skipEditorContext && currentTarget?.canProvideContext) {
		handleEditorContextAnalytics({
			isFirstPrompt,
			source,
			contentContext: contentContextValue,
			userPreference: !!addCurrentPageAsContextUserPreference,
			sendAnalyticsEvent,
			activeTargetId: !!activeTargetId,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
		});
	}

	const whiteboardContext = await chatContextStoreActions.getWhiteboardContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const slidesContext = await chatContextStoreActions.getSlidesContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const databaseContext = await chatContextStoreActions.getDatabaseContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const dashboardEditorContext = await chatContextStoreActions.getDashboardEditorContext({
		contentContext: contentContextValue,
		activeTargetId,
	});

	const browserContext = await chatContextStoreActions.getBrowserContext({
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		enabled: true,
	});

	const jiraContext = await (async () => {
		// Only fetch Jira context when in staging area and product is Jira
		// and not the first prompt (refinement messages)
		if (!isFirstPrompt && contentContextValue === 'staging-area' && productKey === 'jira') {
			return chatContextStoreActions.getJiraContext?.();
		}
		return undefined;
	})();

	const additionalCreationContext = editorContext?.additionalContext;
	const additionalCreationContextParams = creationContextParams?.additionalContext;
	const bridgeAdditionalContext = additionalContextReceiverId
		? await chatContextStoreActions.getAdditionalContext(additionalContextReceiverId)
		: undefined;
	if (editorContext && editorContext.additionalContext) {
		delete editorContext.additionalContext;
	}
	const useGenericEditorSkill = editorContext?.useGenericEditorSkill;
	if (editorContext && 'useGenericEditorSkill' in editorContext) {
		delete editorContext.useGenericEditorSkill;
	}

	const isViewMode =
		contentContextValue === 'staging-area'
			? !!editorContext?.isViewMode
			: !!(
					editorContext?.isViewMode ||
					whiteboardContext?.isViewMode ||
					slidesContext?.isViewMode ||
					databaseContext?.isViewMode
				);

	const editorContentType = getChatContextContentType({
		editorContext,
		whiteboardContext,
		databaseContext,
		slidesContext,
	});
	const aiFeatureContext = chatContextStoreActions.getAIFeatureContext();
	const hasSurfaceContext = !!(
		editorContext ||
		whiteboardContext ||
		slidesContext ||
		databaseContext
	);
	const isNewViewRoutingEnabled =
		productKey === 'confluence' &&
		!skipEditorContext &&
		!hasSurfaceContext &&
		isExperimentEnabled('aifc_creation_context_stale_target_fix');
	const publishedView = aiFeatureContext?.confluence_view_context;
	const hasSelectedPageView =
		isNewViewRoutingEnabled &&
		contentContextValue === 'global' &&
		activeTargetId === undefined &&
		!currentTarget?.canProvideContext &&
		addCurrentPageAsContextUserPreference === true &&
		creationContextParams?.isViewMode !== false &&
		typeof publishedView === 'object' &&
		publishedView !== null &&
		'contentId' in publishedView &&
		typeof publishedView.contentId === 'string' &&
		'contentType' in publishedView &&
		publishedView.contentType === 'page';
	// The control cohort retains the original MAUI-on inference when no bridge is registered.
	const legacyPageView = productKey === 'confluence' && !skipEditorContext && !hasSurfaceContext;
	const creationContextIsViewMode = expValEquals('cc-maui-experiment', 'isEnabled', true)
		? creationContextParams?.isViewMode !== undefined
			? creationContextParams.isViewMode
			: isViewMode || (isNewViewRoutingEnabled ? hasSelectedPageView : legacyPageView)
		: isNewViewRoutingEnabled && creationContextParams?.isViewMode !== undefined
			? creationContextParams.isViewMode
			: isViewMode || hasSelectedPageView;
	const isDraftLockedForEditing = !!editorContext?.isDraftLockedForEditing;
	const isRemoveBackground =
		productKey === 'confluence' &&
		creationContextParams?.additionalContext?.imageEditAction === 'removeBackground' &&
		UNSAFE_expValNoExposure('cc_image_remove_background', 'isEnabled', false);
	const remixContext = isRemoveBackground
		? {
				...creationContextParams,
				mediaCollection: creationContextParams?.additionalContext?.mediaCollection,
			}
		: editorContext;
	const contentMauiId = remixContext?.contentMauiId ?? creationContextParams?.contentMauiId;
	const mediaFileId = remixContext?.mediaFileId ?? creationContextParams?.mediaFileId;
	const dynamicUiType =
		remixContext?.dynamicUiType ||
		creationContextParams?.dynamicUiType ||
		lastAssistantMessageDynamicUiType;
	// The collection id for the selected media node, sent so the backend can resolve the
	// current media's collection when editing an existing image (image-create action).
	const mediaCollection = remixContext?.mediaCollection;
	const creationContextSource =
		contentMauiId || mediaFileId
			? 'remix'
			: // If source is explicitly passed, use that, otherwise rely on creationContextParams.source which is set at creation entry point (e.g. 'remix', 'ai-first-creation')
				(source ?? creationContextParams?.source);
	// The context store returns the raw content context; the outbound
	// creationContext experience/contentTypes are attached here when the request is built.
	const creationContextExperience = getCreationContextExperience({
		contentContextValue: contentContextValue,
		isFirstPrompt,
		source,
		creationContextSource: creationContextParams?.source,
		existingExperience: creationContextParams?.experience,
		existingContentTypes: creationContextParams?.contentTypes,
		chatContextContentType: editorContentType ?? (hasSelectedPageView ? 'page' : undefined),
		isRemixAction,
		isViewMode: creationContextIsViewMode,
		contentMauiId,
		mediaFileId,
		isCreationContextContractEnabled,
	});

	// A remix edit must always carry the block being edited and its source, so these cannot stay
	// behind an unrelated experiment once the contract is enforced.
	const isRemixEditContract =
		isCreationContextContractEnabled &&
		(creationContextExperience?.experience ?? creationContextParams?.experience) === 'remix_edit';

	const creationContextBlocks = getCreationContextBlocks({
		existingBlocks: creationContextParams?.blocks,
		experience: creationContextExperience?.experience ?? creationContextParams?.experience,
		source: creationContextSource,
		contentMauiId,
		mediaFileId,
		dynamicUiType: remixContext?.dynamicUiType ?? creationContextParams?.dynamicUiType,
		isCreationContextContractEnabled,
	});
	const stagingAreaEntryPoint = chatContextStoreActions.getStagingAreaEntryPoint();
	const isFormsOrSurveysSmartCreate =
		contentContextValue === 'staging-area' &&
		((stagingAreaEntryPoint === 'jsmFormsRovoCreate' && fg('soju-5536-forms-skill')) ||
			(stagingAreaEntryPoint === 'jsmSurveyRovoCreate' &&
				fg('phx_stop_confluence_page_create_when_create_survey')));
	const isAgentCreationSmartCreate =
		stagingAreaEntryPoint === 'rovo-agent-create-modal' &&
		fg('sltns-1529-draft-agent-non-consequential');
	const creationContext = {
		...(creationContextParams ? creationContextParams : {}),
		// When the rearchitecture gate is on and we're fixing a staging-area first-prompt
		// (user opened Rovo chat on a live page they just created via CWR), the staging-area
		// store state is stale. The user is chatting about the page they're editing — not
		// running a CWR create flow — so isStagingArea should be false.
		isStagingArea: isStagingAreaFirstPromptFix ? false : contentContextValue === 'staging-area',
		productKey,
		// When `contentMauiId` or `mediaFileId` is present, we don't need to send the source
		// Ideally we want to set it upstream, but we need to ensure we don't break existing functionality
		source: creationContextSource,
		isViewMode: creationContextIsViewMode,
		isDraftLockedForEditing,
		useGenericEditorSkill,
		additionalContext:
			additionalCreationContext ||
			additionalCreationContextParams ||
			bridgeAdditionalContext ||
			mediaCollection
				? {
						...additionalCreationContext,
						...additionalCreationContextParams,
						...(mediaCollection ? { mediaCollection } : {}),
						...bridgeAdditionalContext,
					}
				: undefined,
		...(jiraContext && { jiraContext }),
		...((expValEquals('cc-maui-experiment', 'isEnabled', true) ||
			isRemixEditContract ||
			isRemoveBackground) && {
			...(remixContext?.dynamicUiSource && {
				dynamicUiSource: {
					...remixContext.dynamicUiSource,
					...(creationContextParams?.dynamicUiSource?.fragmentAdf && {
						fragmentAdf: creationContextParams.dynamicUiSource.fragmentAdf,
					}),
				},
			}),
			...((isRemixAction ||
				source === 'remix' ||
				creationContextParams?.source === 'remix' ||
				isRemixEditContract) && {
				dynamicUiType,
			}),
			...(remixContext?.dynamicUiSubtype && {
				dynamicUiSubtype: remixContext.dynamicUiSubtype,
			}),
			contentMauiId,
			mediaFileId,
		}),
		...(creationContextBlocks && { blocks: creationContextBlocks }),
		// Derive experience and contentTypes for CWR staging-area follow-ups and global chat context.
		...creationContextExperience,
		// JSM Forms and Surveys use their own skill routing for initial prompts and refinements.
		...((isFormsOrSurveysSmartCreate || isAgentCreationSmartCreate) && {
			experience: null,
			isStagingArea: false,
		}),
	};

	// A remix edit targets a block, not the content hosting it, so the contract requires every
	// surface context to be absent. The context is still resolved above, because it carries the
	// MAUI identifiers that classify the edit in the first place.
	const sendsSurfaceContext = !isRemixEditContract;

	// Bookmark
	const artifactContext = chatContextStoreActions.getArtifactContext?.();

	return {
		browserContext,
		editorContext: sendsSurfaceContext ? editorContext : undefined,
		whiteboardContext: sendsSurfaceContext ? whiteboardContext : undefined,
		slidesContext: sendsSurfaceContext ? slidesContext : undefined,
		databaseContext: sendsSurfaceContext ? databaseContext : undefined,
		dashboardEditorContext,
		additionalContext: {
			spaceCreateContext: await chatContextStoreActions.getSpaceCreateContext({
				isFirstPrompt,
				contentContext: contentContextValue,
			}),
			creationContext:
				isCreationContextContractEnabled || fg('confluence_aifc_new_live_page_chat_context')
					? applyCreationContextContract(creationContext)
					: creationContext,
			userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			search_artifact: searchArtifact,
		},
		computedExperience:
			isFormsOrSurveysSmartCreate || isAgentCreationSmartCreate
				? undefined
				: creationContextExperience?.experience,
		aiFeatureContext:
			productKey === 'confluence' &&
			publishedView &&
			!hasSelectedPageView &&
			isExperimentEnabled('aifc_creation_context_stale_target_fix')
				? { ...aiFeatureContext, confluence_view_context: undefined }
				: aiFeatureContext,
		jiraWorkflowContext: chatContextStoreActions.getJiraWorkflowContext(),
		dashboardContext: chatContextStoreActions.getDashboardContext(),
		artifactContext,
		useCurrentBrowsingContext: chatContextStoreActions.useCurrentBrowsingContext(
			!!addCurrentPageAsContextUserPreference,
		),
	};
};
